<template>
  <div class="carousel-container" v-press="navigateToSeries">

    <div :class="['carousel-inner', dimmed ? 'dimmed' : '']">
      <TG4Image
        :image="image"
        aspect="landscape"
        size="xLarge"
        :badge="series.badge"
        :alt="series.name"
      />
      <div class="overlay">
        <h2 class="series-title">{{ series.name }}</h2>
        <p v-if="tagline" class="series-tagline">{{ tagline }}</p>
        <div class="watch-now">{{ $t('home.watchNow') }}</div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">


import { computed, getCurrentInstance } from 'vue';
import { SeriesResponse } from '@/types/cms';
import { Language } from '@/enums/language';
import { useStore } from '@/composables/useStore';
import { RouteName, routeTo } from '@/router/routes';
import TG4Image from '@/components/TG4Image.vue';

const props = defineProps<{
  series: SeriesResponse;

  dimmed?: boolean;
}>();

const { useGetter } = useStore();
const currentLanguage = useGetter<string>('currentLanguage');

const instance = getCurrentInstance();
const router = instance?.proxy?.$router;

const image = computed(() => props.series.heroImage ?? props.series.mainImage);

const tagline = computed(() => {
  const { taglineEn, taglineGa, descEn, descGa } = props.series;
  return currentLanguage.value === Language.Irish
    ? taglineGa ?? descGa
    : taglineEn ?? descEn;
});

const navigateToSeries = () => {
  router?.push(routeTo(RouteName.Series, { slugOrVID: props.series.slug }));
};
</script>

<style scoped lang="scss">
.carousel-container {
  position: relative;
  box-sizing: border-box;
  flex-shrink: 0;
  width: $carousel-card-width;
  overflow: hidden;

  border: $focus-border-width solid transparent;

  &:focus {
    outline: 0;
    border-color: $color-pink;
  }
}

.carousel-inner {
  position: relative;
  width: 100%;
  transition: opacity 0.2s ease-in-out;

  &.dimmed {
    opacity: 0.4;
  }
}

.overlay {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 140px 60px 48px 60px;
  text-align: left;
  @include text-white;

  background: linear-gradient(
    to top,
    rgba(0, 0, 0, 0.95) 0%,
    rgba(0, 0, 0, 0.65) 45%,
    rgba(0, 0, 0, 0) 100%
  );
}

.series-title {
  margin: 0;
  @include text-title;
  @include truncate;
}

.series-tagline {
  margin: 12px 0 0 0;
  @include text-body;
  @include line-clamp(2);
}

.watch-now {
  display: inline-block;
  margin-top: 28px;
  padding: 14px 40px;
  background-color: $color-pink;
  color: $color-off-white;
  @include text-button;
}
</style>
